import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { UserService } from "@app2/account/user.service";
import { PercolatorClientService } from "@app2/clients/percolator-client.service";
import { SearchClientService } from "@app2/clients/search-client.service";
import { EventsPageService } from "@app2/search/events-page/events-page.service";
import { DEFAULT_CUSTOM_COLUMNS } from "@app2/search/search-shared/constants";
import { byField } from "@app2/util/util";
import * as _ from "lodash-es";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { Domain, FRAUD_DOMAIN } from "@app2/alert/constants";
import { FRAUD_IMPORTANT_FIELDS } from "@app2/alert/alert-inbox/constants";

export interface SearchResultsTableEntry {
    id: string;
    timestamp: number;
    message: string;
    data: SearchResultsTableEntryData[];
    isSelected: boolean;
    detailsVisible: boolean;
}

export interface SearchResultsTableEntryData {
    key: string;
    value: any[];
    important: boolean;
    orderKey: string;
}

export interface ImportantFieldData {
    key: string;
    value: Set<any>;
    category?: string;
}

interface SimilarityDifferenceCounts {
    sameFieldValuesAsAnchorCount: number;
    differentFieldValuesAsAnchorCount: number;
}

export interface WrappedEntry {
    id: string;
    timestamp: number;
    data: SearchResultsTableEntryData[];
    isSelected: boolean;
    detailsVisible: boolean;
    unmodifiedEntry: any;
    similarityDifferenceCounts: {
        sameFieldValuesAsAnchorCount: number; //This is only very briefly uninitialized.
        differentFieldValuesAsAnchorCount: number;
    };
    isFraudDomain: boolean;
}

const presetImportantFields = [
    "message",
    "ip_src",
    "ip_dest",
    "src_port",
    "dest_port",
    "sensor_name",
    "hostname",
    "api_key",
    "category",
    "severity",
    "app_name",
    "protocol",
    "logger", // for internal use -- name of log4j logger

    "account_domain",
    "account_name",
    "account_type",
    "action",
    "channel",
    "command_line",
    "connection_service",
    "connection_duration",
    "connection_sent_bytes",
    "connection_response_bytes",
    "dhcp_assigned_ip",
    "dpd_analyzer",
    "dpd_failure",
    "dns_query",
    "dns_qclass_name",
    "dns_qtype_name",
    "dns_answers",
    "dns_ttls",
    "geo_dest.city",
    "geo_dest.country",
    "geo_src.city",
    "geo_src.country",
    "hash",
    "hash_type",
    "image",
    "image_loaded",
    "mac_address",
    "new_account_domain",
    "new_account_name",
    "parent_command_line",
    "parent_image",
    "parent_process_id",
    "process_name",
    "process_id",
    "signature",
    "signed",
    "source_module_name",
    "subject",
    "subject_user_name",
    "subject_domain_name",
    "target_domain",
    "target_user_name",
    "user",
    "user_domain",
    "user_name",
    "weird_name",
    "weird_additional",
];

@Component({
    selector: "search-results-table",
    template: `
        <table dsTable id="search-results" class="hover-table full-width" *ngIf="entries.length > 0">
            <thead>
            <tr>
                <th class="hover-tab"></th>
                <th class="selector"
                    (click)="toggleAllSelected()"
                    *ngIf="selectable">
                    <i class="fal fa-check-square" *ngIf="allSelected"></i>
                    <i class="fal fa-square" *ngIf="!allSelected"></i>
                </th>
                <th class="chevron"></th>
                <th class="timestamp"
                    (click)="changeSortField('timestamp')"
                    [class]="{ 'default-cursor': !this.sortable }">
                    {{"TIMESTAMP" | localize}}
                    <sort-order-arrows fieldName="timestamp"
                                       [currentField]="sortOptions?.name"
                                       [currentDesc]="sortOptions?.desc"></sort-order-arrows>
                </th>
                <th *ngIf="simdifEnabled" class="sim-diff-column">{{"SIM" | localize}}</th>
                <th *ngIf="simdifEnabled" class="sim-diff-separator-column">|</th>
                <th *ngIf="simdifEnabled" class="sim-diff-column">{{"DIFF" | localize}}</th>
                <th *ngFor="let fieldName of customColumns;" dsResizableColumn
                    [attr.colName]="'custom-column-' + fieldName"
                    (click)="changeSortField(fieldName)"
                    [class]="getCustomColumnClass(fieldName)"
                    [attr.test-class]="'custom-column-header'">
                    {{ getNameForColumn(fieldName) }}
                    <sort-order-arrows [fieldName]="fieldName"
                                       [currentField]="sortOptions?.name"
                                       [currentDesc]="sortOptions?.desc"></sort-order-arrows>
                </th>
                <th></th>
            </tr>
            </thead>
            <tbody *ngFor="let entry of wrappedEntries; index as i"
                   [attr.test-id]="'search-result-table-' + i">
            <tr (click)="entry.detailsVisible = !entry.detailsVisible"
                [class]="{'active': entry.isSelected, 'closed': !entry.detailsVisible, 'active-tab': entry.detailsVisible}"
                test-class="search-result-header">
                <td class="hover-tab"></td>
                <td *ngIf="selectable" class="selector"
                    (click)="toggleSelection(entry); $event.stopPropagation();"
                    [attr.test-id]="'select-' + entry.id">
                    <i class="fal fa-check-square" *ngIf="entry.isSelected"></i>
                    <i class="fal fa-square" *ngIf="!entry.isSelected"></i>
                </td>
                <td class="chevron">
                    <i class="far"
                       [ngClass]="{'fa-chevron-up': entry.detailsVisible, 'fa-chevron-down': !entry.detailsVisible}"></i>
                </td>
                <!-- stopPropagation so the row is not expanded/collapsed -->
                <td class="timestamp" test-class="timestamp-column" (click)="$event.stopPropagation()" *dsPermissionsRequired="'EVENT_VIEW'">
                    <timestamp-dropdown [timestamp]="entry.timestamp"></timestamp-dropdown>
                </td>
                <td class="timestamp" test-class="timestamp-column" *dsPermissionsRequired="'!EVENT_VIEW'">
                    <timestamp-dropdown [timestamp]="entry.timestamp"></timestamp-dropdown>
                </td>
                <td *ngIf="simdifEnabled" class="sim-diff-column">
                    <a class="boxed-badge similar-fields-count-button"
                       [matTooltip]="'PERFORM_SEARCH_ON_OVERLAPPING_FIELDS' | localize"
                       [matTooltipPosition]="'after'"
                       dsRouterLink="/search"
                       [queryParams]="{ query: getFieldIntersectionQueryStateParams(entry) }"
                       (click)="$event.stopPropagation();">
                    <span>
                    {{entry.similarityDifferenceCounts.sameFieldValuesAsAnchorCount}}
                    </span>
                    </a>
                </td>
                <td *ngIf="simdifEnabled" class="sim-diff-separator-column">|</td>
                <td *ngIf="simdifEnabled" class="sim-diff-column">
                    <span *ngIf="entry === anchorEvent">
                        <i class="fa fa-anchor"></i>
                    </span>
                    <span *ngIf="entry !== anchorEvent">
                        <span (click)="selectAnchorEvent(entry); $event.stopPropagation();"
                              [matTooltip]="'SET_ANCHOR_FOR_COMPARISONS' | localize"
                              [matTooltipPosition]="'after'"
                              class="boxed-badge different-fields-count-button">
                            {{entry.similarityDifferenceCounts.differentFieldValuesAsAnchorCount}}
                        </span>
                    </span>
                </td>
                <td *ngFor="let fieldName of customColumns"
                    [class]="getCustomColumnClass(fieldName)"
                    [attr.test-class]="fieldName + '-column'">
                    <span *ngIf="fieldName.includes('_timestamp')">{{ getValueForColumn(entry, fieldName)|timestamp }}</span>
                    <span *ngIf="!(fieldName.includes('_timestamp'))">{{ getValueForColumn(entry, fieldName) }}</span>
                </td>
                <td></td>
            </tr>
            <tr *ngIf="entry.detailsVisible" [class]="{expanded: entry.detailsVisible}"
                class="no-hover">
                <td class="empty-data"></td>
                <td class="empty-data"></td>
                <td [colSpan]="detailsColWidth" class="search-result-details">
                    <search-results-table-detailed-content [entry]="entry"
                                                           [filterable]="filterable"
                                                           [copyable]="copyable"
                                                           [simdifEnabled]="simdifEnabled"
                                                           [anchorEventSet]="anchorEventSet"
                                                           (toggleFilter)="toggleFilter.emit($event)">
                    </search-results-table-detailed-content>
                </td>
            </tr>
            </tbody>
        </table>
    `,
})
export class SearchResultsTableComponent implements OnInit, OnChanges {
    @Input() entries: any;
    @Input() customColumns: string[];
    @Input() sortOptions: any;
    @Input() filterable: boolean;
    @Input() selectable: boolean;
    @Input() copyable: boolean;
    @Input() sortable: boolean;
    @Input() simdifEnabled: boolean = false;

    @Output() toggleFilter = new EventEmitter<any>();
    @Output() changeSortOptions = new EventEmitter<{ name: string, desc: boolean }>();
    @Output() importantFieldDataChanged = new EventEmitter<ImportantFieldData[]>();

    private allSelected: boolean = false;
    anchorEvent: any;
    anchorEventSet: Set<any> = new Set();
    detailsColWidth: number;
    private wrappedEntries: WrappedEntry[] = [];
    private sortableFields: string[];
    private importantFields: string[];

    constructor(@Inject(PRODUCT_DOMAIN) private inboxDomain: Domain,
                private readonly user: UserService,
                private readonly eventsPageService: EventsPageService,
                private readonly percolatorClient: PercolatorClientService,
                private readonly searchClient: SearchClientService) {
    }

    ngOnInit() {
        this.detailsColWidth = (this.selectable ? 3 : 2) + this.customColumns.length + (this.simdifEnabled ? 3 : 0);
        this.customColumns = _.isEmpty(this.customColumns) ? this.defaultCustomColumns() : _.cloneDeep(this.customColumns);
        this.percolatorClient.getQueries()
            // @ts-ignore
            .then((queries) => _.chain(queries).pluck("applyMetadata").map(_.keys).flatten().unique().value())
            .catch(() => [])
            .then(keys => this.importantFields = _.union(keys, this.inboxDomain === FRAUD_DOMAIN ? FRAUD_IMPORTANT_FIELDS : presetImportantFields));
        // Sorting by 'message' could cause a timeout in ES
        this.searchClient.getSortableFields().then(fields =>
            this.sortableFields = fields.filter(field => field !== "message"));
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.simdifEnabled) {
            // This is done up here as well in case a user only toggles the "Similarities/Differences button"
            this.detailsColWidth = (this.selectable ? 3 : 2) + this.customColumns.length + (this.simdifEnabled ? 3 : 0);
        }
        if (!changes.entries) {
            return;
        }
        const newEntries = changes.entries.currentValue || [];
        const oldEntryMap = this.wrappedEntries.reduce((memo, entry: { id: string }) => {
            memo[entry.id] = entry;
            return memo;
        }, {});

        this.importantFields = this.inboxDomain === FRAUD_DOMAIN ? FRAUD_IMPORTANT_FIELDS : presetImportantFields;
        this.wrappedEntries = newEntries.map(entry => this.toWrappedEntry(entry, oldEntryMap));
        if (this.wrappedEntries.length > 0) {
            this.selectAnchorEvent(this.wrappedEntries[0]);

            if (this.inboxDomain === FRAUD_DOMAIN) {
                // Takes the key and values of all data entries with an important field
                let importantData = this.wrappedEntries.map(entry => entry.data.filter(data => data.important)).flat()
                    .map(data => ({ key: data.key, value: data.value }));
                // Combines values of data entries with the same key and removes duplicates
                let cleanedImportantData: ImportantFieldData[] = Object.values(importantData.reduce((acc, obj) => {
                    if (!acc[obj.key]) {
                        acc[obj.key] = { ...obj, value: obj.value};
                    } else if (!acc[obj.key].value.find(value => obj.value.includes(value))) {
                        acc[obj.key].value = acc[obj.key].value.concat(obj.value);
                    }
                    return acc;
                }, {}));
                this.importantFieldDataChanged.emit(cleanedImportantData);
            }
        }

        const selectedEntries = this.wrappedEntries
            .filter(entry => entry.isSelected)
            .map(entry => entry.unmodifiedEntry);
        this.eventsPageService.replaceEvents(selectedEntries);
        this.allSelected = false;

        // The details rows need to span as many columns as we have in the other rows
        // Columns taken into account here: hover, selector and timestamp.
        if (_.isEmpty(this.customColumns)) {
            this.customColumns = this.defaultCustomColumns();
        }
        this.detailsColWidth = (this.selectable ? 3 : 2) + this.customColumns.length + (this.simdifEnabled ? 3 : 0);
    }

    toggleAllSelected() {
        this.allSelected = !this.allSelected;

        this.wrappedEntries.forEach(wrappedEntry => {
            if (wrappedEntry.isSelected !== this.allSelected) {
                this.eventsPageService.selectEvent(wrappedEntry.unmodifiedEntry);
                wrappedEntry.isSelected = this.allSelected;
            }
        });
    }

    private defaultCustomColumns(): string[] {
        return [...DEFAULT_CUSTOM_COLUMNS];
    }

    private selectAnchorEvent(entry: any) {
        this.anchorEvent = entry;
        this.anchorEventSet = new Set();

        this.getDisplayableData(entry.unmodifiedEntry).forEach(datum => {
            this.anchorEventSet.add(this.getKeyValueAsString(datum));
        });
        this.wrappedEntries.forEach((e: WrappedEntry) => {
            e.similarityDifferenceCounts = this.getSimilarityDifferenceCounts(e.data);
        });
    }

    private orderedData(data: SearchResultsTableEntryData[]): SearchResultsTableEntryData[] {
        return data
            .sort(byField("orderKey"))
            .sort((a, b): number => {
                // cast bools to int
                return +b.important - +a.important;
            });
    }

    changeSortField(fieldName: string): void {
        if (!this.sortable || !this.sortableFields.includes(fieldName)) {
            return;
        }

        if (this.sortOptions?.name === fieldName && !this.sortOptions?.desc) {
            this.changeSortOptions.emit({ name: fieldName, desc: true });
        } else {
            this.changeSortOptions.emit({ name: fieldName, desc: false });
        }
    }

    // returns CSS classes
    getCustomColumnClass(fieldName: string): string {
        const cursorClass = this.sortableFields?.includes(fieldName) ? "" : "default-cursor";

        if (fieldName === "message") {
            // Setting a fixed width avoids collapsing the 'message' column when there are too many custom columns
            const subClass = this.customColumns.length < 4 ? "message-full-width" : "message-fixed-width";
            return `message ${ subClass } ${ cursorClass }`;
        } else {
            return `custom-column ${ cursorClass }`;
        }
    }

    getNameForColumn(fieldName: string): string {
        const dotIndex = fieldName.lastIndexOf(".");
        if (dotIndex === -1) {
            return fieldName;
        }
        // Shorten field names with dot notation "geo_src.location.lon" becomes "lon"
        return fieldName.substr(dotIndex + 1);
    }

    toggleSelection(entry) {
        this.eventsPageService.selectEvent(entry.unmodifiedEntry);
        entry.isSelected = !entry.isSelected;
        this.allSelected = false;
    }

    getFieldIntersectionQueryStateParams(entry: WrappedEntry) {
        const ignoredColumns = new Set(["message", "timestamp", "ingest_timestamp", "original_message", "praesidio_parse_nanos"]);
        return entry.data
            .filter(datum => !ignoredColumns.has(datum.key) &&
                this.anchorEventSet.has(this.getKeyValueAsString(datum)))
            .map(fieldValue => fieldValue.key + ':"' + fieldValue.value + '"')
            .join(" AND ");
    }

    getKeyValueAsString(entry: SearchResultsTableEntryData): string {
        return entry.key + "-" + entry.value;
    }

    getValueForColumn(entry: WrappedEntry, fieldName: string): any {
        if (fieldName.indexOf(".") === -1) {
            return entry.unmodifiedEntry[fieldName];
        }
        // Parse field with dot notation
        const parts = fieldName.split(".");
        const firstPart = parts.shift();
        return parts.reduce((acc, part) => acc && acc[part],
            entry.unmodifiedEntry[firstPart]);
    }

    isNormalField(key: string): boolean {
        return key !== "duration" && key !== "duration_unit";
    }

    // hit is ES specific lingo
    private toWrappedEntry(hit, oldEntryMap: Record<string, WrappedEntry>) {
        const oldEntry = oldEntryMap[hit._id];
        return {
            id: hit._id,
            timestamp: hit.timestamp,
            data: this.getDisplayableData(hit),
            isSelected: oldEntry ? oldEntry.isSelected : false,
            detailsVisible: oldEntry ? oldEntry.detailsVisible : false,
            unmodifiedEntry: hit,
            similarityDifferenceCounts: {
                sameFieldValuesAsAnchorCount: 0, //This is only very briefly uninitialized.
                differentFieldValuesAsAnchorCount: 0,
            },
            isFraudDomain: this.inboxDomain === FRAUD_DOMAIN,
        };
    }

    private getDisplayableData(unmodifiedEntry: object) {
        const enumerateObj = (value: any, field: any): SearchResultsTableEntryData[] => {
            if (Array.isArray(value)) {
                // @ts-ignore
                return _.map(value, oneValue => {
                    return enumerateObj(oneValue, field);
                });
            }
            if (_.isObject(value)) {
                // This must come after isArray, since in JavaScript, arrays are objects.
                // @ts-ignore
                return _.map(_.toPairs(value), (pair) => {
                    return enumerateObj(pair[1], field + "." + pair[0]);
                });
            }
            if (field === "additional_fields") {
                // If this was a document indexed before ingestion-service-7.118 it will have been caught by
                // Array.isArray above and recursively passed as a single value here. So here we may either have a
                // simple string or a JSON-string representation of an array.
                try {
                    return enumerateObj(JSON.parse(value), field);
                } catch (e) {
                    // wasn't a JSON-string array
                }
            }
            const important = this.importantFields.includes(field);
            const orderKey = (<string>field).split(".", 2)[0]; // so that nested objects retain their order
            return [{ orderKey: orderKey, key: field, important: important, value: [value] }];
        };
        let result = Object.entries(unmodifiedEntry)
            // enumerateObj turns a key value pair into an array of SearchResultsTableEntryData
            //
            // For example:
            // timestamp -> 348293489
            //      to
            // [{key: "timestamp", value: 348293489, orderKey: "timestamp", important: false}]
            //
            // or
            //
            // installed_applications -> [{name: "Microsoft Office", version:"1.0.5"},{name: "Visual Studio", version: "1.0"}]
            //      to
            // [{key:"installed_applications.name", value:"Microsoft Office", orderKey:"installed_applications", important:false},
            //  {key:"installed_applications.version", value:"1.0.5", orderKey:"installed_applications", important:false},
            //  {key:"installed_applications.name", value:"Visual Studio"
            //     ... etc
            // ]
            //
            // Note: the return type of this function is actually any number of nested SearchResultsTableEntryData
            // arrays. The recursive flatten results in a single-depth SearchResultsTableEntryData array
            .map(([field, value]) => enumerateObj(value, field))
            .flat(Infinity)
            .flat(1)
            .filter(datum => datum.key !== "_id");

        if (this.inboxDomain === FRAUD_DOMAIN) {
            result = Object.values(result.reduce((acc, obj) => {
                if (!acc[obj.key]) {
                    acc[obj.key] = { ...obj, value: obj.value };
                } else {
                    acc[obj.key].value = acc[obj.key].value.concat(obj.value);
                }
                return acc;
            }, {}));
        }

        return this.orderedData(result);
    }

    private getSimilarityDifferenceCounts(data: SearchResultsTableEntryData[]): SimilarityDifferenceCounts {
        let sameCount = 0;
        let differenceCount = 0;
        data.forEach(datum => {
            if (this.anchorEventSet.has(this.getKeyValueAsString(datum))) {
                sameCount += 1;
            } else {
                differenceCount += 1;
            }
        });
        return {
            differentFieldValuesAsAnchorCount: differenceCount,
            sameFieldValuesAsAnchorCount: sameCount,
        };
    }
}
